/**
 * Wiegeartikel: Gewicht und Preis (Verkauf nach kg mit festem Kilopreis).
 *
 * Gewichte sind intern IMMER ganze Gramm (1 … GEWICHT_MAX_GRAMM), Preise ganze
 * Cent (Regel 5). Das Modul ist rein: keine Gleitkommarechnung für Beträge,
 * keine Abhängigkeiten – es wird von API, Kasse und Backoffice gemeinsam genutzt.
 * Die Rundungsregel ist in docs/wiegeartikel/rundung.md beschrieben.
 */

/** Höchstes Gewicht einer Wiegeposition in Gramm (99,999 kg). */
export const GEWICHT_MAX_GRAMM = 99_999;

function pruefeGramm(gramm: number): void {
  if (!Number.isInteger(gramm) || gramm < 1 || gramm > GEWICHT_MAX_GRAMM) {
    throw new RangeError(
      `Gewicht muss eine ganze Grammzahl von 1 bis ${String(GEWICHT_MAX_GRAMM)} sein, erhalten: ${String(gramm)}`,
    );
  }
}

/**
 * Positionsbetrag in Cent für `gramm` zum Kilopreis `kilopreisCent`.
 *
 * Rechnung: gramm × kilopreisCent / 1000, kaufmännisch gerundet (halbe Cent
 * vom Nullpunkt weg). Gerechnet wird mit BigInt, damit auch große Kilopreise
 * exakt bleiben; das Ergebnis muss eine sichere Ganzzahl sein.
 *
 * Beispiele (Kilopreis 5,99 €): 125 g → 75, 750 g → 449, 1250 g → 749, 2000 g → 1198.
 * Negative Kilopreise (Gutschrift) werden symmetrisch gerundet.
 */
export function gewichtPreisCent(gramm: number, kilopreisCent: number): number {
  pruefeGramm(gramm);
  if (!Number.isSafeInteger(kilopreisCent)) {
    throw new TypeError(
      `Kilopreis muss ein ganzzahliger Cent-Wert sein, erhalten: ${String(kilopreisCent)}`,
    );
  }
  const produkt = BigInt(gramm) * BigInt(kilopreisCent);
  const negativ = produkt < 0n;
  const betrag = negativ ? -produkt : produkt;
  // + 500 vor der Ganzzahldivision = halbe Cent aufrunden (auf dem Betrag, also vom Nullpunkt weg)
  const gerundet = (betrag + 500n) / 1000n;
  const ergebnis = Number(negativ ? -gerundet : gerundet);
  if (!Number.isSafeInteger(ergebnis)) {
    throw new RangeError('Positionsbetrag übersteigt den darstellbaren Bereich');
  }
  return ergebnis;
}

/** Erlaubte Eingabe: Kilogramm mit Komma oder Punkt, höchstens drei Nachkommastellen. */
const EINGABE = /^(\d*)(?:[.,](\d{0,3}))?$/;

/**
 * Liest eine Gewichtseingabe in Kilogramm ('0,750', '1.25', '2', ',5') und
 * liefert ganze Gramm. Ungültig, leer, 0, negativ, mehr als drei
 * Nachkommastellen oder über GEWICHT_MAX_GRAMM → null.
 *
 * Bewusst ohne parseFloat: Ziffern werden als Text zerlegt, damit lange
 * Eingaben weder gerundet noch in Exponentialschreibweise umgedeutet werden.
 */
export function grammAusEingabe(text: string): number | null {
  const treffer = EINGABE.exec(text.trim());
  if (treffer === null) return null;
  const ganz = treffer[1] ?? '';
  const nachkomma = treffer[2] ?? '';
  if (ganz === '' && nachkomma === '') return null;
  const ganzOhneNullen = ganz.replace(/^0+/, '');
  // mehr als zwei Stellen vor dem Komma sind sicher über 99,999 kg
  if (ganzOhneNullen.length > 2) return null;
  const gramm = Number(ganzOhneNullen || '0') * 1000 + Number(nachkomma.padEnd(3, '0'));
  if (gramm < 1 || gramm > GEWICHT_MAX_GRAMM) return null;
  return gramm;
}

/** Formatiert Gramm als Kilogramm mit drei Nachkommastellen: 750 → '0,750 kg'. */
export function formatiereKg(gramm: number): string {
  if (!Number.isSafeInteger(gramm)) {
    throw new TypeError(`Gewicht muss eine ganze Grammzahl sein, erhalten: ${String(gramm)}`);
  }
  const negativ = gramm < 0;
  const absolut = Math.abs(gramm);
  const kg = String(Math.trunc(absolut / 1000));
  const rest = String(absolut % 1000).padStart(3, '0');
  return `${negativ ? '-' : ''}${kg},${rest} kg`;
}

/** Menge der Belegposition in kg (gramm / 1000, höchstens drei Nachkommastellen). */
export function mengeKgAusGramm(gramm: number): number {
  pruefeGramm(gramm);
  return gramm / 1000;
}
